// SettingsPlanOverviewCards.jsx

import React from 'react';

/**
 * SettingsPlanOverviewCards
 *
 * Renders the current plan summary card and current usage summary card.
 *
 * @param {string} apiUsageError - API usage load error text.
 * @param {boolean} apiUsageLoading - Whether API usage is loading.
 * @param {object} apiUsageSummary - Derived API usage summary model.
 * @param {string} billingRenewsText - Formatted billing renewal text.
 * @param {string} billingStatusText - Formatted billing status text.
 * @param {string} currentPlanPriceText - Formatted current plan price text.
 * @param {string} currentPlanTitle - Active workspace plan title.
 * @param {boolean} isCurrentPlanHiddenFromCatalog - Whether the current plan is hidden from the public catalog.
 * @param {string} liveUsageError - Live usage load error text.
 * @param {boolean} liveUsageLoading - Whether live usage is loading.
 * @param {object} liveUsageSummary - Derived live usage summary model.
 * @param {string} membersUsageText - Formatted members usage text.
 * @param {boolean} shouldShowApiUsage - Whether API usage should be shown.
 * @param {boolean} shouldShowLiveUsage - Whether live usage should be shown.
 * @param {boolean} shouldShowRecordingUsage - Whether recording usage should be shown.
 * @param {string} storageReservedNote - Optional reserved storage note text.
 * @param {string} storageUsageText - Formatted storage usage text.
 * @param {object} theme - Active theme tokens.
 *
 * @returns {ReactNode} The rendered overview cards.
 */
const SettingsPlanOverviewCards = React.memo(({
    apiUsageError,
    apiUsageLoading,
    apiUsageSummary,
    billingRenewsText,
    billingStatusText,
    currentPlanPriceText,
    currentPlanTitle,
    isCurrentPlanHiddenFromCatalog,
    liveUsageError,
    liveUsageLoading,
    liveUsageSummary,
    membersUsageText,
    shouldShowApiUsage,
    shouldShowLiveUsage,
    shouldShowRecordingUsage,
    storageReservedNote,
    storageUsageText,
    theme,
}) => {
    return (
        <>
            {/* CURRENT PLAN */}
            <div
                className="settings-modal-plan-summary-card"
                style={{
                    borderColor: theme.backgroundHover,
                }}
            >
                <div className="settings-modal-plan-summary-header">
                    <div
                        className="settings-modal-plan-summary-title"
                        style={{
                            color: theme.foreground,
                        }}
                    >
                        Current plan
                    </div>
                    <div
                        className="settings-modal-plan-summary-price"
                        style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        {currentPlanPriceText}
                    </div>
                </div>

                <div
                    className="settings-modal-plan-summary-name"
                    style={{
                        color: theme.foreground,
                    }}
                >
                    {currentPlanTitle}
                </div>

                <div
                    className="settings-modal-setting-description"
                    style={{
                        color: theme.foregroundMuted,
                    }}
                >
                    {isCurrentPlanHiddenFromCatalog
                        ? 'This workspace is on a plan that is no longer available for self-serve selection.'
                        : 'Public plans below are available to browse now. Free plans can be activated immediately.'}
                </div>

                <div className="settings-modal-plan-facts">
                    <div className="settings-modal-plan-fact">
                        <span
                            className="settings-modal-plan-fact-label"
                            style={{
                                color: theme.foregroundMuted,
                            }}
                        >
                            Billing status
                        </span>
                        <span
                            className="settings-modal-plan-fact-value"
                            style={{
                                color: theme.foreground,
                            }}
                        >
                            {billingStatusText}
                        </span>
                    </div>

                    <div className="settings-modal-plan-fact">
                        <span
                            className="settings-modal-plan-fact-label"
                            style={{
                                color: theme.foregroundMuted,
                            }}
                        >
                            Billing renews
                        </span>
                        <span
                            className="settings-modal-plan-fact-value"
                            style={{
                                color: theme.foreground,
                            }}
                        >
                            {billingRenewsText}
                        </span>
                    </div>
                </div>
            </div>

            {/* CURRENT USAGE */}
            <div
                className="settings-modal-plan-summary-card"
                style={{
                    borderColor: theme.backgroundHover,
                }}
            >
                <div className="settings-modal-plan-summary-header">
                    <div
                        className="settings-modal-plan-summary-title"
                        style={{
                            color: theme.foreground,
                        }}
                    >
                        Current usage
                    </div>
                    <div
                        className="settings-modal-plan-summary-price"
                        style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        {currentPlanTitle}
                    </div>
                </div>

                <div
                    className="settings-modal-setting-description"
                    style={{
                        color: theme.foregroundMuted,
                    }}
                >
                    {isCurrentPlanHiddenFromCatalog
                        ? 'Current usage is shown below, but public plan limits are unavailable because this workspace is on a retired or hidden plan.'
                        : 'Track current members, storage, API runs, and live usage against the active plan here.'}
                </div>

                <div className="settings-modal-plan-facts">
                    <div className="settings-modal-plan-fact">
                        <span
                            className="settings-modal-plan-fact-label"
                            style={{
                                color: theme.foregroundMuted,
                            }}
                        >
                            Members
                        </span>
                        <span
                            className="settings-modal-plan-fact-value"
                            style={{
                                color: theme.foreground,
                            }}
                        >
                            {membersUsageText}
                        </span>
                    </div>

                    <div className="settings-modal-plan-fact">
                        <span
                            className="settings-modal-plan-fact-label"
                            style={{
                                color: theme.foregroundMuted,
                            }}
                        >
                            Storage
                        </span>
                        <span
                            className="settings-modal-plan-fact-value"
                            style={{
                                color: theme.foreground,
                            }}
                        >
                            {storageUsageText}
                        </span>
                    </div>

                    <div className="settings-modal-plan-fact">
                        <span
                            className="settings-modal-plan-fact-label"
                            style={{
                                color: theme.foregroundMuted,
                            }}
                        >
                            API runs ({apiUsageSummary.monthLabel})
                        </span>
                        <span
                            className="settings-modal-plan-fact-value"
                            style={{
                                color: theme.foreground,
                            }}
                        >
                            {!shouldShowApiUsage
                                ? 'Not included'
                                : apiUsageLoading
                                    ? 'Loading...'
                                    : apiUsageError
                                        ? 'Unavailable'
                                        : apiUsageSummary.summaryText}
                        </span>
                    </div>

                    {shouldShowApiUsage && !apiUsageLoading && !apiUsageError && (
                        <div className="settings-modal-plan-fact">
                            <span
                                className="settings-modal-plan-fact-label"
                                style={{
                                    color: theme.foregroundMuted,
                                }}
                            >
                                API detail
                            </span>
                            <span
                                className="settings-modal-plan-fact-value"
                                style={{
                                    color: theme.foreground,
                                }}
                            >
                                {apiUsageSummary.detailText}
                            </span>
                        </div>
                    )}

                    <div className="settings-modal-plan-fact">
                        <span
                            className="settings-modal-plan-fact-label"
                            style={{
                                color: theme.foregroundMuted,
                            }}
                        >
                            Live mins ({liveUsageSummary.monthLabel})
                        </span>
                        <span
                            className="settings-modal-plan-fact-value"
                            style={{
                                color: theme.foreground,
                            }}
                        >
                            {!shouldShowLiveUsage
                                ? 'Not included'
                                : liveUsageLoading
                                    ? 'Loading...'
                                    : liveUsageError
                                        ? 'Unavailable'
                                        : liveUsageSummary.participantMinutesText}
                        </span>
                    </div>

                    {shouldShowLiveUsage && !liveUsageLoading && !liveUsageError && (
                        <div className="settings-modal-plan-fact">
                            <span
                                className="settings-modal-plan-fact-label"
                                style={{
                                    color: theme.foregroundMuted,
                                }}
                            >
                                Active now
                            </span>
                            <span
                                className="settings-modal-plan-fact-value"
                                style={{
                                    color: theme.foreground,
                                }}
                            >
                                {liveUsageSummary.activeNowText}
                            </span>
                        </div>
                    )}

                    <div className="settings-modal-plan-fact">
                        <span
                            className="settings-modal-plan-fact-label"
                            style={{
                                color: theme.foregroundMuted,
                            }}
                        >
                            Recording mins ({liveUsageSummary.monthLabel})
                        </span>
                        <span
                            className="settings-modal-plan-fact-value"
                            style={{
                                color: theme.foreground,
                            }}
                        >
                            {!shouldShowRecordingUsage
                                ? 'Not included'
                                : liveUsageLoading
                                    ? 'Loading...'
                                    : liveUsageError
                                        ? 'Unavailable'
                                        : liveUsageSummary.recordingMinutesText}
                        </span>
                    </div>
                </div>

                {storageReservedNote && (
                    <div
                        className="settings-modal-note"
                        style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        {storageReservedNote}
                    </div>
                )}
            </div>
        </>
    );
});

SettingsPlanOverviewCards.displayName = 'SettingsPlanOverviewCards';

export default SettingsPlanOverviewCards;